import '../containers/breadcrumbsContainer.scss';
import { Loading, Pager, Text, StartEndDatePicker } from '@components';
import { debounce } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';

// Displays table of breadcrumbs data
// Allows users to pick date range, pagination, and submit button

const debouncedQueryChange = debounce((onQueryChangedFn, args) => {
  onQueryChangedFn(...args);
}, 500);

const BreadcrumbsTable = ({
  locations,
  totalItems,
  hasMore,
  page: initialPage,
  limit: initialLimit,
  startDate: initialStartDate,
  endDate: initialEndDate,
  current,
  isLoading,
  units,
  onRowSelect,
  onQueryChanged,
}) => {
  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [currentlySelected, setCurrentlySelected] = useState(current);
  const [arr, setArr] = useState([]);
  const [disableBtn, setDisableBtn] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const sortOrder = 1;
  const sortField = null;
  const prevTotalItemsRef = useRef(totalItems);
  const scrollRef = useRef(null);

  const checkIfSubmitButtonShouldBeEnabled = () => {
    const bothValid = moment(startDate).isValid() && moment(endDate).isValid();
    setDisableBtn(!(startDate && endDate && bothValid));
  };

  const handleChange = (name, event) => {
    let value = event.target?.value ?? event.toString();
    value = value.replace(/[^0-9]/g, '') || '0';
    if (value.startsWith('0') && value !== '0') value = value.replace(/^0+/, '');
    if (parseInt(value) > totalItems) value = totalItems.toString();

    if (name === 'limit') {
      setLimit(value);
      setPage('1');
    } else if (name === 'page') {
      if (value !== '0') setPage(value);
    }
    if (hasSubmitted && (name !== 'page' || value !== '0')) {
      debouncedQueryChange(onQueryChanged, [
        name === 'page' ? value : page,
        name === 'limit' ? value : limit,
        sortOrder,
        sortField,
        startDate,
        endDate,
      ]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === '-') e.preventDefault();
  };

  const handleDateChange = (name, val) => {
    let updated = moment(val);
    const now = moment();
    const isToday = updated.isSame(now, 'day');

    if (name === 'endDate' && isToday && (!endDate || !endDate.isSame(now, 'day'))) {
      updated.hour(now.hour()).minute(now.minute());
    }

    if (name === 'startDate') setStartDate(updated);
    if (name === 'endDate') setEndDate(updated);

    checkIfSubmitButtonShouldBeEnabled();
  };

  const handleSubmitReport = () => {
    if (prevTotalItemsRef.current !== totalItems) {
      setLimit(totalItems > 50 ? 50 : totalItems);
    } else {
      setLimit(0);
    }

    setPage('1');
    setPendingSubmit(true);
    setHasSubmitted(true);
  };

  useEffect(() => {
    checkIfSubmitButtonShouldBeEnabled();
  }, [startDate, endDate]);

  useEffect(() => {
    setArr(locations);
    setCurrentlySelected(current);
  }, [current, locations]);

  useEffect(() => {
    if (totalItems !== undefined) {
      const newLimit = totalItems > 50 ? 50 : totalItems;
      setLimit(newLimit);
    }
  }, [totalItems]);

  useEffect(() => {
    if (pendingSubmit && hasSubmitted) {
      onQueryChanged('1', 50, sortOrder, sortField, startDate, endDate);
      setPendingSubmit(false);
    }
  }, [limit, pendingSubmit]);

  const pageCount = Math.max(
    0,
    isNaN(parseInt(limit)) || parseInt(limit) === 0
      ? 0
      : Math.ceil(parseInt(totalItems) / parseInt(limit)),
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const nearBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - 100;

      if (!hasMore) return;

      if (nearBottom && !isLoading) {
        const nextPage = (parseInt(page) || 1) + 1;

        setPage(String(nextPage)); // ✅ update state so next scroll uses it
        onQueryChanged(String(nextPage), limit, sortOrder, sortField, startDate, endDate);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [arr.length, totalItems, page, limit, startDate, endDate, isLoading]);

  return (
    <div className="breadcrumb-input-table-upper-wrapper">
      <div className="date-number-input-container ">
        <div className="wrap-date-submit-breadcrumb">
          <StartEndDatePicker
            startDate={startDate}
            endDate={endDate}
            onChangeStartDate={(d) => handleDateChange('startDate', d)}
            onChangeEndDate={(d) => handleDateChange('endDate', d)}
            isBreadCrumbDates={true}
          />
          <Button
            variant="primary"
            className="px-5 float-right submit-report-btn"
            onClick={handleSubmitReport}
            title="Submit Report"
            disabled={disableBtn}
          >
            Submit
          </Button>
        </div>
        {/* Removed after adding infinite scroll/ might add back */}
        {/* <div className="wrap-pager-viewer-breadcrumb d-flex">
          <div className="number-input-container">
            <span>
              <input
                type="number"
                value={limit}
                min={0}
                max={totalItems}
                onKeyDown={handleKeyDown}
                onChange={(e) => handleChange('limit', e)}
              />{' '}
              of {totalItems}
            </span>
          </div>
          <Pager
            page={parseInt(page)}
            pageCount={pageCount}
            onChange={(e) => handleChange('page', e)}
          />
        </div> */}
      </div>

      {isLoading ? (
        <div className="loading-wrapper d-flex justify-content-center">
          <Loading height={220} width={150} />
        </div>
      ) : !hasSubmitted ? null : arr.length === 0 ? ( // Nothing yet (user hasn't submitted)
        <div className="w-100 d-flex flex-column justify-content-center align-items-center text-center py-4">
          <i className="ignore-click sonarIcon sonarIcon-warning fs-2x" />
          <Text as="h5">
            There was no data returned for the submitted timeline.
            <br />
            Try adjusting the date range and submit again.
          </Text>
        </div>
      ) : (
        <div className="scroll-table" ref={scrollRef}>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Distance Moved</th>
              </tr>
            </thead>
            <tbody>
              {arr.map((location) => (
                <tr
                  key={`${location.index}-${currentlySelected}`}
                  onClick={() => onRowSelect(location.index)}
                  className={`cursor-pointer ${
                    location.isHighlighted ? 'cg-table-highlight' : 'cg-table-highlight-default'
                  }`}
                >
                  <td>{moment(location.timestamp).format('L LTS')}</td>
                  <td>
                    {units === 'm'
                      ? Math.round(location.distanceMoved * 3.281 * 0.3048)
                      : Math.round(location.distanceMoved * 3.281)}{' '}
                    {units}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

BreadcrumbsTable.propTypes = {
  locations: PropTypes.array,
  totalItems: PropTypes.number,
  hasMore: PropTypes.bool,
  sortField: PropTypes.string,
  sortOrder: PropTypes.number,
  limit: PropTypes.number,
  page: PropTypes.string,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onRowSelect: PropTypes.func,
  onQueryChanged: PropTypes.func,
  isLoading: PropTypes.bool,
  current: PropTypes.number,
  units: PropTypes.string,
};

export default BreadcrumbsTable;
